/**
 * Configuration serialization utilities for device customization
 * Handles JSON serialization/deserialization and merging with defaults
 */

import { DeviceUICustomization } from '../types/device-config-types';
import { AssetReference } from '../types/asset-types';

/**
 * Serialize device configuration to JSON string
 * Prepares configuration for API storage
 *
 * @param config - Device configuration object
 * @returns JSON string
 */
export function serializeConfig<TIcons = Record<string, AssetReference>>(
  config: DeviceUICustomization<TIcons>
): string {
  try {
    return JSON.stringify(config, null, 2);
  } catch (error) {
    throw new Error(`Failed to serialize configuration: ${error}`);
  }
}

/**
 * Parse device configuration from JSON string
 * Handles errors and validates basic structure
 *
 * @param json - JSON string
 * @returns Parsed configuration object
 */
export function parseConfig<TIcons = Record<string, AssetReference>>(
  json: string
): DeviceUICustomization<TIcons> {
  try {
    const config = JSON.parse(json);

    // Basic validation
    if (!config.deviceType) {
      throw new Error('Missing deviceType in configuration');
    }
    if (!config.formFactor) {
      throw new Error('Missing formFactor in configuration');
    }
    if (!config.theme) {
      throw new Error('Missing theme in configuration');
    }

    return config as DeviceUICustomization<TIcons>;
  } catch (error) {
    throw new Error(`Failed to parse configuration: ${error}`);
  }
}

/**
 * Merge partial configuration with defaults
 * Useful for applying user overrides to default config
 *
 * @param defaults - Default configuration
 * @param overrides - Partial configuration with overrides
 * @returns Merged configuration
 */
export function mergeWithDefaults<TIcons = Record<string, AssetReference>>(
  defaults: DeviceUICustomization<TIcons>,
  overrides: Partial<DeviceUICustomization<TIcons>>
): DeviceUICustomization<TIcons> {
  return {
    ...defaults,
    ...overrides,
    theme: {
      ...defaults.theme,
      ...(overrides.theme || {}),
      colors: {
        ...defaults.theme.colors,
        ...(overrides.theme?.colors || {}),
      },
    },
    layout: {
      ...defaults.layout,
      ...(overrides.layout || {}),
      header: {
        ...defaults.layout.header,
        ...(overrides.layout?.header || {}),
      },
      content: {
        ...defaults.layout.content,
        ...(overrides.layout?.content || {}),
      },
      card: {
        ...defaults.layout.card,
        ...(overrides.layout?.card || {}),
      },
      footer: {
        ...defaults.layout.footer,
        ...(overrides.layout?.footer || {}),
      },
    },
    components: {
      ...defaults.components,
      ...(overrides.components || {}),
      button: {
        ...defaults.components.button,
        ...(overrides.components?.button || {}),
      },
      card: {
        ...defaults.components.card,
        ...(overrides.components?.card || {}),
      },
      badge: {
        ...defaults.components.badge,
        ...(overrides.components?.badge || {}),
      },
      icon: {
        ...defaults.components.icon,
        ...(overrides.components?.icon || {}),
      },
    },
    icons: {
      ...defaults.icons,
      ...(overrides.icons || {}),
    },
    metadata: {
      ...defaults.metadata,
      ...(overrides.metadata || {}),
      modifiedAt: new Date().toISOString(),
    },
  };
}

/**
 * Deep clone configuration object
 * Creates a complete copy to avoid mutation issues
 *
 * @param config - Configuration to clone
 * @returns Cloned configuration
 */
export function cloneConfig<TIcons = Record<string, AssetReference>>(
  config: DeviceUICustomization<TIcons>
): DeviceUICustomization<TIcons> {
  return JSON.parse(JSON.stringify(config));
}

/**
 * Extract UI customization from operator settings
 * Parses settings string and extracts uiCustomization key
 *
 * @param settingsJson - Settings JSON string from API
 * @returns UI customization config or null if not found
 */
export function extractUiCustomization<TIcons = Record<string, AssetReference>>(
  settingsJson: string
): DeviceUICustomization<TIcons> | null {
  try {
    const settings = JSON.parse(settingsJson);
    return settings.uiCustomization || null;
  } catch (error) {
    console.error('Failed to extract UI customization:', error);
    return null;
  }
}

/**
 * Merge UI customization into operator settings
 * CRITICAL: Preserves existing settings fields
 *
 * @param existingSettingsJson - Existing settings JSON string
 * @param uiCustomization - UI customization config to merge
 * @returns Merged settings JSON string
 */
/**
 * Deep merge helper - recursively merges objects
 * Arrays and primitives are replaced, not merged
 */
function deepMerge(target: any, source: any): any {
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return source;
  }

  const result = { ...target };

  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = deepMerge(target[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }

  return result;
}

export function mergeUiCustomizationIntoSettings<TIcons = Record<string, AssetReference>>(
  existingSettingsJson: string,
  uiCustomization: DeviceUICustomization<TIcons>
): string {
  let settings: any = {};

  // Parse existing settings (if any)
  if (existingSettingsJson) {
    try {
      settings = JSON.parse(existingSettingsJson);
    } catch (error) {
      console.warn('Failed to parse existing settings, starting with empty object:', error);
    }
  }

  // Deep merge UI customization (preserves existing nested fields)
  settings.uiCustomization = deepMerge(settings.uiCustomization || {}, uiCustomization);

  // Stringify merged settings
  return JSON.stringify(settings);
}

/**
 * Compare two configurations for equality
 * Useful for detecting unsaved changes
 *
 * @param config1 - First configuration
 * @param config2 - Second configuration
 * @returns True if configurations are equal
 */
export function areConfigsEqual<TIcons = Record<string, AssetReference>>(
  config1: DeviceUICustomization<TIcons>,
  config2: DeviceUICustomization<TIcons>
): boolean {
  // Simple JSON comparison (ignores metadata timestamps)
  const clean1 = { ...config1, metadata: { ...config1.metadata, modifiedAt: '', createdAt: '' } };
  const clean2 = { ...config2, metadata: { ...config2.metadata, modifiedAt: '', createdAt: '' } };

  return JSON.stringify(clean1) === JSON.stringify(clean2);
}

/**
 * Create a diff object showing changes between configurations
 * Useful for displaying what changed to the user
 *
 * @param original - Original configuration
 * @param updated - Updated configuration
 * @returns Object describing changes
 */
export function diffConfigs<TIcons = Record<string, AssetReference>>(
  original: DeviceUICustomization<TIcons>,
  updated: DeviceUICustomization<TIcons>
): {
  hasChanges: boolean;
  changedFields: string[];
} {
  const changedFields: string[] = [];

  // Check top-level fields
  if (original.deviceType !== updated.deviceType) changedFields.push('deviceType');
  if (original.formFactor !== updated.formFactor) changedFields.push('formFactor');

  // Check theme colors
  const originalColors = original.theme.colors;
  const updatedColors = updated.theme.colors;
  Object.keys(updatedColors).forEach((key) => {
    if (originalColors[key as keyof typeof originalColors] !== updatedColors[key as keyof typeof updatedColors]) {
      changedFields.push(`theme.colors.${key}`);
    }
  });

  // Check icons
  const originalIcons = original.icons as Record<string, AssetReference>;
  const updatedIcons = updated.icons as Record<string, AssetReference>;
  Object.keys(updatedIcons).forEach((key) => {
    const origIcon = originalIcons[key];
    const updIcon = updatedIcons[key];
    if (!origIcon || origIcon.assetId !== updIcon.assetId) {
      changedFields.push(`icons.${key}`);
    }
  });

  return {
    hasChanges: changedFields.length > 0,
    changedFields,
  };
}

/**
 * Generate a summary of configuration
 * Useful for displaying config overview to user
 *
 * @param config - Configuration object
 * @returns Human-readable summary
 */
export function generateConfigSummary<TIcons = Record<string, AssetReference>>(
  config: DeviceUICustomization<TIcons>
): string {
  const iconCount = Object.keys(config.icons).length;
  const themeName = config.theme.name || 'Custom Theme';

  return [
    `Device: ${config.deviceType} (${config.formFactor})`,
    `Theme: ${themeName}`,
    `Icons: ${iconCount} configured`,
    `Last modified: ${new Date(config.metadata.modifiedAt).toLocaleString()}`,
  ].join(' | ');
}
